import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ListChecks,
  Download,
  Upload,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { formatCents, type EligiblesView, type EligiblesTabRow } from '@mdu-invoicing/shared';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch, ApiError } from '@/lib/api-client';
import { Card } from '@/components/ui/Card';

/** Sum the PRICED amounts across a set of tab rows (non-priced rows contribute 0). */
function pricedTotal(rows: EligiblesTabRow[]): number {
  return rows.reduce((s, r) => (r.tariff.status === 'PRICED' ? s + r.tariff.amountCents : s), 0);
}

export function Dashboard() {
  const { user, isProvider, canViewAmounts } = useAuth();
  const [view, setView] = useState<EligiblesView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The view is role-scoped server-side (a PROVIDER receives only its own rows).
  useEffect(() => {
    let cancelled = false;
    apiFetch<EligiblesView>('/eligibles')
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const okTotal = useMemo(() => (view ? pricedTotal(view.ssv) + pricedTotal(view.tsa) : 0), [view]);

  const areas = useMemo(() => {
    if (!view) return 0;
    return new Set([...view.ssv, ...view.tsa].map((r) => r.source.areaId ?? '—')).size;
  }, [view]);

  // Every returned row is eligible (the orchestrator filters), so the default
  // proposal is OK — the OK count equals the eligible count until persistence lands.
  const okCount = view ? view.summary.ssvEligible + view.summary.tsaEligible : 0;
  const unpriced = view ? view.summary.ssvUnpriced + view.summary.tsaUnpriced : 0;

  const stats = [
    { label: 'Éligibles SSV', value: view?.summary.ssvEligible ?? 0, icon: ListChecks, tint: 'text-brand-purple bg-brand-purple/10' },
    { label: 'Éligibles TSA', value: view?.summary.tsaEligible ?? 0, icon: ListChecks, tint: 'text-brand-rose bg-brand-rose/10' },
    { label: 'OK à facturer', value: okCount, icon: CheckCircle2, tint: 'text-emerald-600 bg-emerald-50' },
    { label: 'Zones de déploiement', value: areas, icon: MapPin, tint: 'text-brand-orange bg-brand-orange/10' },
  ];

  // Export/Import now live as modals on the Éligibles hub — deep-link with ?action so
  // the quick action opens the right modal. (Staff may also import — verification layer.)
  const actions = [
    { to: '/eligibles', label: 'Voir les éligibles', desc: 'Grille SSV / TSA, filtres et décisions', icon: ListChecks },
    { to: '/eligibles?action=export', label: 'Exporter', desc: 'Fichier de validation partenaire', icon: Download },
    { to: '/eligibles?action=import', label: 'Importer & valider', desc: 'Charger le fichier validé', icon: Upload },
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-slate-500">
          Bonjour <span className="font-semibold text-slate-800">{user?.displayName}</span> — voici le mois de
          facturation en cours.
        </p>
      </div>

      {error ? (
        <Card className="p-5 text-sm text-rose-700">Impossible de charger le tableau de bord : {error}.</Card>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stats.map((s) => (
              <Card key={s.label} className="p-4">
                <div className="flex items-center justify-between">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${s.tint}`}>
                    <s.icon className="h-5 w-5" />
                  </span>
                </div>
                <div className="mt-3 text-2xl font-extrabold tabular-nums text-slate-900">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-300" /> : s.value}
                </div>
                <div className="text-xs font-medium text-slate-500">{s.label}</div>
              </Card>
            ))}
          </div>

          {/* Estimated total + scope */}
          <Card className="flex flex-wrap items-center justify-between gap-3 p-5">
            {canViewAmounts ? (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Montant estimé (enregistrements OK, tarifés)
                </div>
                <div className="mt-1 text-3xl font-extrabold tabular-nums gradient-text">
                  {loading ? '…' : formatCents(okTotal)}
                </div>
                {!loading && unpriced > 0 && (
                  <div className="mt-0.5 text-xs text-amber-600">
                    {unpriced} enregistrement(s) non tarifé(s) — hors §4 ou hors périmètre.
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-400">Montants masqués pour ce compte.</div>
            )}
            <div className="text-right text-xs text-slate-500">
              {isProvider ? (
                <>
                  Périmètre prestataire ·{' '}
                  <span className="font-semibold text-brand-purple">{user?.contractorName}</span>
                </>
              ) : (
                <>Vue Unifiber · tous les contractants</>
              )}
              {view && (
                <div className="mt-0.5">
                  Période : {view.period.from} → {view.period.to}
                </div>
              )}
            </div>
          </Card>
        </>
      )}

      {/* Quick actions */}
      <div className="grid gap-4 sm:grid-cols-3">
        {actions.map((a) => (
          <Link
            key={a.to}
            to={a.to}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-brand-purple/40 hover:bg-slate-50"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-gradient text-white">
              <a.icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{a.label}</span>
                <ArrowRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-rose" />
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{a.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
